import { useCallback, useRef, useState, useEffect } from 'react';

import { useOneTimeOrder } from '../../OneTimeOrderProvider';
import { getTrackerIdEventName, SUBSCRIPTION_TRACKER_TYPES, TRACKER_IDS } from '../../../../config/tracker';
import NavigationBar from '../../../../global/NavigationBar';
import { getCurrentRouteTitle } from '../../../../utils/routes';
import { ROUTE_REWARDS_V2 } from '../../../../config/routes';
import useScrollToTop from '../../../../utils/hooks/useScrollToTop';
import { getOrderCheckoutSession } from '../../../../utils/service';
import { STRIPE_CHECKOUT_SESSION_STATUS } from '../../../../config/stripe';
import GeneralLottie from '../../../../global/GeneralLottie';
import processingPayment from '../../../../animations/processing-payment.json';
import IconUnplug from '../../../../images/icon-unplug.svg';
import { Button, BUTTON_TYPE, BUTTON_SIZE } from '../../../../global/Button';
import { CONTACT_US_URL } from '../../../../config/externalURL';
import MembershipOrderTbbContainer from '../../../MembershipOrderTbb/MembershipOrderTbbContainer';
import { MEMBERSHIP_ORDER_TBB_STEPS, MEMBERSHIP_ORDER_TBB_TYPE } from '../../../MembershipOrderTbb/MembershipOrderTbbContext';
import useNavigateWithSearch from '../../../../../../hooks/useNavigateWithSearch';
import { trackMagellanPurchase } from '../../../../scripts/magellanPixel';
import { ARTSAI_PIXEL_ID } from '../../../../config/config';
import { buildArtsAiPixelUrl, buildMagellanPurchaseMetadata } from '../../../../utils/trackingPixels';

import './OneTimeOrderConfirmation.scss';

const ONE_TIME_ORDER_CHECK_TIMEOUT = 1500;
const ONE_TIME_ORDER_CHECK_TRIES = 10;

const {
  oneTimeOrder: {
    confirmation: {
      [SUBSCRIPTION_TRACKER_TYPES.VIEW]: trackerView,
      [SUBSCRIPTION_TRACKER_TYPES.CLOSE]: trackerClose,
    },
  },
} = TRACKER_IDS;

const OneTimeOrderConfirmation = () => {
  useScrollToTop();
  const verificationTimeoutRef = useRef(null);
  const navigateWithSearch = useNavigateWithSearch();

  const {
    isFromCheckout,
    checkoutSessionId,
    posthog,
  } = useOneTimeOrder();

  const [isCheckingPayment, setIsCheckingPayment] = useState(
    isFromCheckout && !!checkoutSessionId,
  );
  const [subscriptionCheckTries, setSubscriptionCheckTries] = useState(ONE_TIME_ORDER_CHECK_TRIES);
  const [checkoutSessionStatus, setCheckoutSessionStatus] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [checkoutSessionPurchaseData, setCheckoutSessionPurchaseData] = useState({
    amount: 0,
    currency: 'USD',
    orderId: checkoutSessionId,
  });

  const verifyCheckoutSession = useCallback(async () => {
    const {
      data: checkoutSessionData,
      error: checkoutSessionError,
    } = await getOrderCheckoutSession(checkoutSessionId);

    if (checkoutSessionError || !checkoutSessionData?.status) {
      setSelectedItems({});
      setSelectedAddress(null);
      setCheckoutSessionStatus(null);
      setCheckoutSessionPurchaseData({
        amount: 0,
        currency: 'USD',
        orderId: checkoutSessionId,
      });
      return;
    }

    const purchaseAmount = (
      checkoutSessionData.order?.totalAmount ??
      checkoutSessionData.order?.totalPrice?.amount ??
      checkoutSessionData.order?.amount ??
      checkoutSessionData.amount ??
      0
    );
    const purchaseCurrency = (
      checkoutSessionData.order?.currency ??
      checkoutSessionData.order?.currencyCode ??
      checkoutSessionData.currency ??
      'USD'
    );
    const purchaseOrderId = checkoutSessionData.order?.id ?? checkoutSessionId;

    setSelectedItems(
      checkoutSessionData.order?.products
        ? checkoutSessionData.order.products.reduce((acc, product) => ({
          ...acc,
          [product.productType]: product.quantity,
        }), {})
        : {},
    );
    setSelectedAddress(checkoutSessionData.order?.shippingAddress ?? null);
    setCheckoutSessionPurchaseData({
      amount: Number(purchaseAmount),
      currency: purchaseCurrency,
      orderId: purchaseOrderId,
    });
    setCheckoutSessionStatus(checkoutSessionData.status);
  }, [checkoutSessionId]);

  const handleOnTryAgainClick = useCallback(() => {
    setSubscriptionCheckTries(ONE_TIME_ORDER_CHECK_TRIES);
    setCheckoutSessionStatus(null);
    setIsCheckingPayment(true);
  }, []);

  const handleClose = useCallback(() => {
    posthog.capture(getTrackerIdEventName(trackerClose));
    navigateWithSearch(ROUTE_REWARDS_V2, {}, false);
  }, [posthog, navigateWithSearch]);

  useEffect(() => {
    if (isCheckingPayment) {
      if (subscriptionCheckTries > 0) {
        if (checkoutSessionStatus === STRIPE_CHECKOUT_SESSION_STATUS.COMPLETE) {
          trackMagellanPurchase({
            ...checkoutSessionPurchaseData,
            metadata: buildMagellanPurchaseMetadata('takebackbag'),
          });
          const artsAiPurchaseUrl = buildArtsAiPixelUrl({
            action: 'purchase',
            pixelId: ARTSAI_PIXEL_ID,
            value: checkoutSessionPurchaseData?.amount ?? 0,
            orderId: checkoutSessionPurchaseData?.orderId ?? checkoutSessionId,
            contentId: 'takebackbag',
          });
          const artsAiImg = new Image();
          artsAiImg.src = artsAiPurchaseUrl;
          setIsCheckingPayment(false);
          clearTimeout(verificationTimeoutRef.current);
        } else if (checkoutSessionStatus === STRIPE_CHECKOUT_SESSION_STATUS.EXPIRED) {
          setIsCheckingPayment(false);
          clearTimeout(verificationTimeoutRef.current);
        } else {
          verificationTimeoutRef.current = setTimeout(async () => {
            await verifyCheckoutSession();
            setSubscriptionCheckTries(prev => prev - 1);
          }, ONE_TIME_ORDER_CHECK_TIMEOUT);
        }
      } else {
        setIsCheckingPayment(false);
        clearTimeout(verificationTimeoutRef.current);
      }
    }

    return () => {
      if (verificationTimeoutRef.current) {
        clearTimeout(verificationTimeoutRef.current);
      }
    };
  }, [
    isCheckingPayment,
    subscriptionCheckTries,
    checkoutSessionStatus,
    checkoutSessionPurchaseData,
  ]);

  useEffect(() => {
    posthog.capture(getTrackerIdEventName(trackerView));
  }, []);

  if (isCheckingPayment) {
    return (
      <GeneralLottie
        animationData={processingPayment}
        backgroundColor="var(--trashie-ui-faded-black-10)"
      />
    );
  }

  if (!isCheckingPayment && checkoutSessionStatus !== STRIPE_CHECKOUT_SESSION_STATUS.COMPLETE) {
    return (
      <div className="OneTimeOrderConfirmation">
        <div className="OneTimeOrderConfirmation__container">
          <img
            className="OneTimeOrderConfirmation__container-icon"
            src={IconUnplug}
            alt="Unplug"
          />
          <div className="OneTimeOrderConfirmation__container-title">
            Your payment was successful but our system is having difficulties
            to activate your order.
          </div>
          <Button
            type={BUTTON_TYPE.QUATERNARY}
            size={BUTTON_SIZE.LARGE}
            onClick={handleOnTryAgainClick}
            className="OneTimeOrderConfirmation__container-button"
          >
            Try again
          </Button>
          <div className="OneTimeOrderConfirmation__container-message">
            Still not working?
          </div>
          <Button
            type={BUTTON_TYPE.LINK_QUINARY}
            onClick={() => window.open(CONTACT_US_URL, '_blank')}
          >
            Contact support
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="OneTimeOrderConfirmation">
      <NavigationBar
        className="OneTimeOrderConfirmation__navigationBar"
        title={getCurrentRouteTitle(true)}
        onClose={handleClose}
      />
      <MembershipOrderTbbContainer
        containerClassName="OneTimeOrderConfirmation__section"
        orderConfirmationClassName="OneTimeOrderConfirmation__orderConfirmation"
        defaultStep={MEMBERSHIP_ORDER_TBB_STEPS.ORDER_CONFIRMATION}
        defaultValues={selectedItems}
        defaultSelectedAddress={selectedAddress}
        useWhiteCards
        floatingFooter={false}
        showOrderConfirmationPromoSection
        orderType={MEMBERSHIP_ORDER_TBB_TYPE.ONE_TIME}
      />
    </div>
  );
};

export default OneTimeOrderConfirmation;
